import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Landmark,
  TrendingUp,
  TrendingDown,
  FileWarning,
  ArrowRight,
  Receipt,
} from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { money, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUS_STYLE = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sent: "bg-blue-50 text-blue-700 border-blue-200",
  overdue: "bg-rose-50 text-rose-700 border-rose-200",
  draft: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function Dashboard() {
  const { currentId, current } = useCompany();
  const CURRENT_YEAR = new Date().getFullYear();
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const yearOptions = Array.from({ length: 6 }, (_, i) => String(CURRENT_YEAR - i));

  useEffect(() => {
    if (!currentId) return;
    setLoading(true);
    const start = `${year}-01-01`;
    const end =
      Number(year) === CURRENT_YEAR
        ? new Date().toISOString().slice(0, 10)
        : `${year}-12-31`;
    Promise.all([
      api.get("/dashboard", { params: { start, end } }),
      api.get("/invoices"),
    ])
      .then(([d, inv]) => {
        setData(d.data);
        setInvoices(inv.data);
      })
      .finally(() => setLoading(false));
  }, [currentId, year, CURRENT_YEAR]);

  const outstanding = invoices
    .filter((i) => i.display_status !== "paid" && i.display_status !== "draft")
    .sort((a, b) => (b.display_status === "overdue" ? 1 : 0) - (a.display_status === "overdue" ? 1 : 0))
    .slice(0, 6);

  const periodLabel = Number(year) === CURRENT_YEAR ? "YTD" : year;
  const cards = [
    {
      label: `Net Income (${periodLabel})`,
      value: money(data?.net_income),
      hint: `${money(data?.total_income)} income − ${money(data?.total_expense)} expenses`,
      icon: TrendingUp,
      primary: true,
    },
    {
      label: `Net GST Owing (${periodLabel})`,
      value: money(data?.net_gst_owing),
      hint: `${money(data?.gst_collected)} collected − ${money(data?.itc)} ITCs`,
      icon: Landmark,
    },
    {
      label: "Outstanding Invoices",
      value: money(data?.outstanding),
      hint: `${data?.overdue_count || 0} overdue · ${data?.draft_count || 0} draft`,
      icon: FileWarning,
    },
    {
      label: `Expenses (${periodLabel})`,
      value: money(data?.expenses_total),
      hint: "Incl. GST paid",
      icon: TrendingDown,
    },
  ];

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
            Dashboard
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {current?.name} · {Number(year) === CURRENT_YEAR ? "Year-to-date snapshot" : `${year} full year`}
          </p>
        </div>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger data-testid="dash-year-select" className="w-32 rounded-md">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {yearOptions.map((y) => (
              <SelectItem key={y} value={y} data-testid={`dash-year-${y}`}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <Card
            key={i}
            data-testid={`dash-card-${i}`}
            className={`p-5 rounded-lg border shadow-sm ${
              c.primary ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200"
            }`}
          >
            <div className="flex items-center justify-between">
              <span
                className={`text-xs uppercase tracking-wide ${
                  c.primary ? "text-slate-300" : "text-slate-400"
                }`}
              >
                {c.label}
              </span>
              <c.icon className={`h-4 w-4 ${c.primary ? "text-slate-300" : "text-slate-400"}`} />
            </div>
            {loading ? (
              <Skeleton className="h-9 w-32 mt-3" />
            ) : (
              <div className="font-heading text-3xl font-bold mt-2 tabular-nums">{c.value}</div>
            )}
            <div className={`text-xs mt-1 tabular-nums ${c.primary ? "text-slate-400" : "text-slate-500"}`}>
              {c.hint}
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <Card className="lg:col-span-2 p-0 rounded-lg border-slate-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
            <h2 className="font-heading font-semibold text-slate-900">Outstanding Invoices</h2>
            <Link
              to="/invoices"
              data-testid="dash-view-invoices"
              className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1"
            >
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {loading ? (
            <div className="p-5 space-y-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : outstanding.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm">
              No outstanding invoices. 🎉
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="px-5 py-2 font-medium">Invoice</th>
                  <th className="px-5 py-2 font-medium">Client</th>
                  <th className="px-5 py-2 font-medium">Due</th>
                  <th className="px-5 py-2 font-medium text-right">Balance</th>
                  <th className="px-5 py-2 font-medium text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {outstanding.map((inv) => (
                  <tr key={inv.id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-5 py-2.5 font-medium text-slate-900">{inv.invoice_number}</td>
                    <td className="px-5 py-2.5 text-slate-600">{inv.client_name}</td>
                    <td className="px-5 py-2.5 text-slate-600">{fmtDate(inv.due_date)}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums font-medium">
                      {money(inv.balance_due)}
                    </td>
                    <td className="px-5 py-2.5 text-right">
                      <Badge
                        variant="outline"
                        className={`capitalize ${STATUS_STYLE[inv.display_status]}`}
                      >
                        {inv.display_status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card className="p-5 rounded-lg border-slate-200 shadow-sm">
          <h2 className="font-heading font-semibold text-slate-900 mb-4">GST · {year}</h2>
          <div className="space-y-4">
            <Row label="GST Collected" value={money(data?.gst_collected)} icon={TrendingUp} tone="text-emerald-600" />
            <Row label="Input Tax Credits" value={`− ${money(data?.itc)}`} icon={Receipt} tone="text-blue-600" />
            <div className="border-t border-slate-200 pt-4 flex items-center justify-between">
              <span className="font-medium text-slate-900">Net Owing</span>
              <span className="font-heading text-xl font-bold tabular-nums text-slate-900">
                {money(data?.net_gst_owing)}
              </span>
            </div>
            <Link
              to="/gst"
              data-testid="dash-open-gst"
              className="mt-2 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
            >
              Open GST return module <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value, icon: Icon, tone }) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-sm text-slate-600">
        <Icon className={`h-4 w-4 ${tone}`} /> {label}
      </span>
      <span className="tabular-nums font-medium text-slate-900">{value}</span>
    </div>
  );
}
